import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Label, Message } from 'semantic-ui-react';
import {
  getPortabilidadeMultiTitularInfo,
  getPortabilidadeTipoLabel,
  listRepresentantesReutilizaveis,
  resolvePortabilidadeTipo,
  type PortabilidadeLineFields,
  type PortabilidadeRepresentanteFields,
  type PortabilidadeTipo,
} from '../../utils/portabilidadeContractRules';
import { clientService, type ReceitaWsCompanyData } from '../../services/clients/ClientService';
import { formatCpfProgressive, cleanCpf, toBrazilNationalPhoneDigits } from '../../services/utils/stringUtils';

type AbaPortabilidade = 'pf-pj' | 'cnpj-diferente' | 'cnpj-cnpj';

export type PortabilidadeFieldValue = string | PortabilidadeRepresentanteFields[];

export interface PortabilidadeDadosFormProps {
  linha: PortabilidadeLineFields;
  onChange: (field: keyof PortabilidadeLineFields, value: PortabilidadeFieldValue) => void;
  /** Outras linhas do contrato (para reutilizar representante). */
  outrasLinhas?: PortabilidadeLineFields[];
  /** Opcional: unificar o titular PF→CNPJ da linha atual em todas as linhas PF→CNPJ do contrato. */
  onUnificarPortanteEmTodas?: (source: PortabilidadeLineFields) => void;
  readOnly?: boolean;
  showValidationHint?: boolean;
}

interface ReceitaQsaItem {
  nome?: string;
  qual?: string;
  nomeRepLegal?: string;
  qualRepLegal?: string;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px',
  borderRadius: '4px',
  border: '1px solid #ccc',
};

const abaFromTipo = (tipo: PortabilidadeTipo): AbaPortabilidade => {
  if (tipo === 'pf-pj') return 'pf-pj';
  if (tipo === 'cnpj-diferente') return 'cnpj-diferente';
  return 'cnpj-cnpj';
};

const formatCnpj = (digits: string) => {
  const d = digits.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

export const PortabilidadeDadosForm: React.FC<PortabilidadeDadosFormProps> = ({
  linha,
  onChange,
  outrasLinhas = [],
  onUnificarPortanteEmTodas,
  readOnly = false,
  showValidationHint = true,
}) => {
  const tipo = resolvePortabilidadeTipo(linha);
  const [aba, setAba] = useState<AbaPortabilidade>(() => abaFromTipo(tipo));
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjError, setCnpjError] = useState<string | null>(null);
  const [socios, setSocios] = useState<ReceitaQsaItem[]>([]);
  const [selectedSocioIdx, setSelectedSocioIdx] = useState('');
  const [selectedReuseIdx, setSelectedReuseIdx] = useState('');
  const [draftRep, setDraftRep] = useState({ nome: '', cpf: '' });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef<Map<string, ReceitaWsCompanyData>>(new Map());

  useEffect(() => {
    const next = abaFromTipo(tipo);
    if (tipo !== 'ambiguo') setAba(next);
  }, [tipo]);

  const tipoLabel = getPortabilidadeTipoLabel(tipo);
  const currentRep = (linha.portabilidadeRepresentantes ?? [])[0] ?? null;

  useEffect(() => {
    if (currentRep) {
      setDraftRep({
        nome: currentRep.nome ?? '',
        cpf: currentRep.cpf ?? '',
      });
    }
  }, [currentRep?.nome, currentRep?.cpf]);

  const reutilizaveis = useMemo(
    () => listRepresentantesReutilizaveis(outrasLinhas, linha.id),
    [outrasLinhas, linha.id]
  );

  const multiTitularInfo = useMemo(() => {
    const all = linha.id ? [...outrasLinhas, linha] : [...outrasLinhas, linha];
    return getPortabilidadeMultiTitularInfo(all);
  }, [outrasLinhas, linha]);

  const switchAba = (next: AbaPortabilidade) => {
    if (readOnly || next === aba) return;
    setAba(next);
    if (next === 'pf-pj') {
      onChange('portabilidadeCnpjCedente', '');
      onChange('portabilidadeRazaoSocialCedente', '');
      onChange('portabilidadeRepresentantes', []);
      setDraftRep({ nome: '', cpf: '' });
    } else if (next === 'cnpj-diferente') {
      onChange('portabilidadeNomeCompleto', '');
      onChange('portabilidadeCpf', '');
    } else {
      onChange('portabilidadeNomeCompleto', '');
      onChange('portabilidadeCpf', '');
      onChange('portabilidadeCnpjCedente', '');
      onChange('portabilidadeRazaoSocialCedente', '');
      onChange('portabilidadeRepresentantes', []);
      setDraftRep({ nome: '', cpf: '' });
      setSocios([]);
    }
  };

  const applyCedenteData = (company: ReceitaWsCompanyData) => {
    const razao = (company.nome || company.fantasia || '').trim();
    if (razao) {
      onChange('portabilidadeRazaoSocialCedente', razao);
    }
    setSocios(company.qsa || []);
  };

  const fetchCedente = async (cnpjDigits: string) => {
    if (cacheRef.current.has(cnpjDigits)) {
      applyCedenteData(cacheRef.current.get(cnpjDigits)!);
      return;
    }

    setCnpjLoading(true);
    setCnpjError(null);
    try {
      const receita = await clientService.getCnpjData(cnpjDigits);
      cacheRef.current.set(cnpjDigits, receita);
      applyCedenteData(receita);
    } catch (e: any) {
      setCnpjError(
        e?.response?.data?.message || e?.message || 'Falha ao consultar CNPJ do cedente na ReceitaWS.'
      );
      setSocios([]);
    } finally {
      setCnpjLoading(false);
    }
  };

  const onCnpjChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 14);
    onChange('portabilidadeCnpjCedente', formatCnpj(digits));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (digits.length === 14) {
      debounceRef.current = setTimeout(() => {
        void fetchCedente(digits);
      }, 500);
    } else {
      setSocios([]);
      setCnpjError(null);
    }
  };

  const setRepresentante = (rep: PortabilidadeRepresentanteFields | null) => {
    if (!rep) {
      onChange('portabilidadeRepresentantes', []);
      setDraftRep({ nome: '', cpf: '' });
      return;
    }
    const nome = rep.nome.trim();
    const cpfDigits = cleanCpf(rep.cpf);
    if (!nome || cpfDigits.length !== 11) return;
    const formatted = { nome, cpf: formatCpfProgressive(rep.cpf) };
    onChange('portabilidadeRepresentantes', [formatted]);
    setDraftRep(formatted);
    setCnpjError(null);
  };

  const applyFromQsa = () => {
    const idx = Number(selectedSocioIdx);
    if (Number.isNaN(idx) || !socios[idx]) return;
    const nome = (socios[idx].nome || '').trim();
    setDraftRep({ nome, cpf: '' });
    setSelectedSocioIdx('');
    setCnpjError('Nome do representante preenchido pelo QSA. Informe o CPF completo e confirme.');
  };

  const applyFromReuse = () => {
    const idx = Number(selectedReuseIdx);
    if (Number.isNaN(idx) || !reutilizaveis[idx]) return;
    setRepresentante(reutilizaveis[idx]);
    setSelectedReuseIdx('');
  };

  const confirmDraftRep = () => {
    setRepresentante(draftRep);
  };

  const clearRep = () => setRepresentante(null);

  const operadoraMissing = !(linha.portabilidadeOperadora ?? '').trim();
  const linhaMissing = (linha.portabilidadeLinha ?? '').replace(/\D/g, '').length < 10;
  const badgeColor =
    tipo === 'pf-pj' ? 'purple' : tipo === 'cnpj-diferente' ? 'teal' : tipo === 'cnpj-cnpj' ? 'blue' : 'grey';

  const abaBtn = (id: AbaPortabilidade, label: string) => (
    <button
      type="button"
      disabled={readOnly}
      onClick={() => switchAba(id)}
      style={{
        padding: '8px 12px',
        borderRadius: '6px',
        border: aba === id ? '2px solid #2185d0' : '1px solid #ccc',
        background: aba === id ? '#e8f4fd' : '#fff',
        fontWeight: aba === id ? 700 : 500,
        cursor: readOnly ? 'default' : 'pointer',
        fontSize: '12px',
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        background: '#f0f7ff',
        border: '1px solid #2185d0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '8px',
      }}
    >
      <div
        style={{
          fontWeight: 'bold',
          color: '#2185d0',
          marginBottom: '8px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <span>Dados da Portabilidade</span>
        <Label size="tiny" color={badgeColor}>
          Será tratado como {tipoLabel}
        </Label>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {abaBtn('pf-pj', 'CPF → CNPJ')}
        {abaBtn('cnpj-diferente', 'CNPJ → CNPJ (empresa diferente)')}
        {abaBtn('cnpj-cnpj', 'Mesmo CNPJ')}
      </div>

      {multiTitularInfo && (
        <Message info size="tiny" style={{ marginBottom: '12px' }}>
          {multiTitularInfo}
        </Message>
      )}

      <div
        style={{
          background: '#e8f4fd',
          border: '1px solid #b3d9f7',
          borderRadius: '6px',
          padding: '10px 12px',
          marginBottom: '12px',
          fontSize: '12px',
          color: '#1a5276',
          lineHeight: 1.5,
        }}
      >
        {aba === 'pf-pj' && (
          <span>
            Preencha <strong>Nome e CPF</strong> do titular da linha (pessoa física). O PDF incluirá o
            bloco Responsáveis Portabilidade com âncora própria de assinatura.
          </span>
        )}
        {aba === 'cnpj-diferente' && (
          <span>
            Informe o <strong>CNPJ Cedente</strong> e <strong>1 representante</strong> (QSA = representante;
            só o nome vem da ReceitaWS). Pode reutilizar um representante já usado em outra linha do contrato.
          </span>
        )}
        {aba === 'cnpj-cnpj' && (
          <span>
            Portabilidade do <strong>mesmo CNPJ</strong> do contrato — só operadora e linha a portar são
            obrigatórias.
          </span>
        )}
      </div>

      {aba === 'pf-pj' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          {!readOnly && onUnificarPortanteEmTodas && (
            <div style={{ gridColumn: '1 / -1', marginBottom: '4px' }}>
              <Button
                type="button"
                size="small"
                basic
                color="blue"
                onClick={() => onUnificarPortanteEmTodas(linha)}
              >
                Usar este titular em todas as linhas PF→CNPJ
              </Button>
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
              Nome Completo <span style={{ color: '#e53935' }}>*</span>
            </label>
            <input
              placeholder="Nome completo do portante"
              value={linha.portabilidadeNomeCompleto ?? ''}
              readOnly={readOnly}
              onChange={(e) => onChange('portabilidadeNomeCompleto', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
              CPF <span style={{ color: '#e53935' }}>*</span>
            </label>
            <input
              placeholder="000.000.000-00"
              value={linha.portabilidadeCpf ?? ''}
              readOnly={readOnly}
              maxLength={14}
              onChange={(e) => onChange('portabilidadeCpf', formatCpfProgressive(e.target.value))}
              style={inputStyle}
            />
          </div>
        </div>
      )}

      {aba === 'cnpj-diferente' && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
                CNPJ Cedente <span style={{ color: '#e53935' }}>*</span>
              </label>
              <input
                placeholder="00.000.000/0000-00"
                value={linha.portabilidadeCnpjCedente ?? ''}
                readOnly={readOnly}
                maxLength={18}
                onChange={(e) => onCnpjChange(e.target.value)}
                style={inputStyle}
              />
              {cnpjLoading && (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Consultando ReceitaWS…</div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
                Razão Social Cedente <span style={{ color: '#e53935' }}>*</span>
              </label>
              <input
                placeholder="Razão social da empresa cedente"
                value={linha.portabilidadeRazaoSocialCedente ?? ''}
                readOnly={readOnly}
                onChange={(e) => onChange('portabilidadeRazaoSocialCedente', e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {cnpjError && (
            <Message warning size="tiny" style={{ marginBottom: '10px' }}>
              {cnpjError}
            </Message>
          )}

          {!readOnly && reutilizaveis.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
                  Usar representante de outra linha
                </label>
                <select
                  value={selectedReuseIdx}
                  onChange={(e) => setSelectedReuseIdx(e.target.value)}
                  style={{ ...inputStyle, height: '36px' }}
                >
                  <option value="">Selecione…</option>
                  {reutilizaveis.map((r, i) => (
                    <option key={`${r.cpf}-${i}`} value={String(i)}>
                      {r.nome} — {formatCpfProgressive(r.cpf)}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="button" size="small" primary disabled={!selectedReuseIdx} onClick={applyFromReuse}>
                Reutilizar
              </Button>
            </div>
          )}

          {!readOnly && socios.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
                  Representante (QSA ReceitaWS)
                </label>
                <select
                  value={selectedSocioIdx}
                  onChange={(e) => setSelectedSocioIdx(e.target.value)}
                  style={{ ...inputStyle, height: '36px' }}
                >
                  <option value="">Selecione o representante…</option>
                  {socios.map((s, i) => (
                    <option key={`${s.nome}-${i}`} value={String(i)}>
                      {s.nome || 'Representante sem nome'}
                      {s.qual ? ` — ${s.qual}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="button" size="small" primary disabled={!selectedSocioIdx} onClick={applyFromQsa}>
                Usar nome
              </Button>
            </div>
          )}

          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
            Representante do cedente <span style={{ color: '#e53935' }}>*</span>
            <span style={{ fontWeight: 400, color: '#666', marginLeft: '6px' }}>(máx. 1 por linha)</span>
          </div>

          {!readOnly ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '8px', marginBottom: '10px' }}>
              <input
                placeholder="Nome do representante"
                value={draftRep.nome}
                onChange={(e) => setDraftRep((p) => ({ ...p, nome: e.target.value }))}
                style={inputStyle}
              />
              <input
                placeholder="CPF do representante *"
                value={draftRep.cpf}
                maxLength={14}
                onChange={(e) =>
                  setDraftRep((p) => ({ ...p, cpf: formatCpfProgressive(e.target.value) }))
                }
                style={inputStyle}
              />
              <Button
                type="button"
                size="small"
                primary
                disabled={!draftRep.nome.trim() || cleanCpf(draftRep.cpf).length !== 11}
                onClick={confirmDraftRep}
              >
                Confirmar
              </Button>
              <Button type="button" size="small" disabled={!currentRep} onClick={clearRep}>
                Limpar
              </Button>
            </div>
          ) : null}

          {currentRep ? (
            <div style={{ fontSize: '13px', marginBottom: '8px' }}>
              Confirmado: <strong>{currentRep.nome}</strong> — {currentRep.cpf}
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
              Nenhum representante confirmado nesta linha.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
            Operadora de Origem <span style={{ color: '#e53935' }}>*</span>
          </label>
          <input
            placeholder="Ex: Claro, Vivo, Oi..."
            value={linha.portabilidadeOperadora ?? ''}
            readOnly={readOnly}
            onChange={(e) => onChange('portabilidadeOperadora', e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>
            Linha a Portar <span style={{ color: '#e53935' }}>*</span>
          </label>
          <input
            placeholder="Número completo com DDD"
            value={linha.portabilidadeLinha ?? ''}
            readOnly={readOnly}
            onChange={(e) =>
              onChange('portabilidadeLinha', toBrazilNationalPhoneDigits(e.target.value))
            }
            style={inputStyle}
          />
        </div>
      </div>

      {tipo === 'ambiguo' && showValidationHint && (
        <div style={{ fontSize: '12px', color: '#e53935', marginTop: '8px' }}>
          Preencha só PF (nome+CPF) ou só cedente (CNPJ+razão), sem misturar nem deixar parcial.
        </div>
      )}
      {(operadoraMissing || linhaMissing) && showValidationHint && (
        <div style={{ fontSize: '12px', color: '#e53935', marginTop: '8px' }}>
          {operadoraMissing && 'Operadora de origem é obrigatória. '}
          {linhaMissing && 'Linha a portar é obrigatória (DDD + número).'}
        </div>
      )}
    </div>
  );
};

export default PortabilidadeDadosForm;
